import { dungApiThat, goiApi, goiApiTepHoacLienKet } from '../api/http';
import { mayChuChungTu } from '../../mock/ocr';
import type { DocumentType } from '../../modules/cau-hinh-ocr/types';
import type {
  BulkResult,
  DocDetail,
  DocJobStatus,
  DocPage,
  DocPatch,
  DocReprocessInput,
  DocStats,
  DocStatus,
  DocTranscript,
  DocUploadAccepted,
  DocUploadInput,
  OcrStage,
  TepXemTruoc,
} from '../../modules/xu-ly/types';
import { GOC_OCR, giaLap, giaLapDanhSach, kiemTrang, maHoa } from './ocr-chung';

const GOC = `${GOC_OCR}/documents`;
const TRANG_RONG: DocPage = { items: [], next_cursor: null };
const THONG_KE_RONG: DocStats = {
  total: 0,
  draft: 0,
  processing: 0,
  confirmed: 0,
  processed: 0,
  transferred: 0,
  error: 0,
  processing_by_stage: { QUEUED: 0, READING: 0, READ_DONE: 0, MAPPING: 0, RETRY_WAIT: 0, UNKNOWN: 0 },
};

export interface LocChungTu {
  search?: string;
  status?: DocStatus[];
  ocr_stage?: OcrStage[];
  type?: DocumentType;
  limit: number;
  cursor?: string;
}

const goc = (id: string) => `${GOC}/${maHoa(id)}`;

/**
 * Chứng từ (module `ocr`, đợt 3–4): danh sách, chi tiết, kết quả, tải lên, quét lại, huỷ lượt quét,
 * xác nhận, chuyển, xoá. Mỗi hàm chọn **lúc gọi** giữa API thật và máy chủ giả (`mock/ocr.ts`).
 * Sai trạng thái: `409 OCR_DOCUMENT_STATUS_CONFLICT` với câu backend nói đúng trạng thái cần có.
 */
export const documentRepository = {
  /** `GET /documents` — mới nhất trước; `status`, `ocr_stage` gửi danh sách ngăn bằng dấu phẩy. */
  danhSach: (loc: LocChungTu): Promise<DocPage> =>
    dungApiThat()
      ? goiApi<DocPage>(GOC, {
          query: {
            search: loc.search,
            status: loc.status?.join(','),
            ocr_stage: loc.ocr_stage?.join(','),
            type: loc.type,
            limit: loc.limit,
            cursor: loc.cursor,
          },
        }).then(kiemTrang)
      : giaLapDanhSach(() => mayChuChungTu.danhSach(loc), TRANG_RONG),

  /** `GET /documents/stats`. */
  thongKe: (): Promise<DocStats> =>
    dungApiThat() ? goiApi<DocStats>(`${GOC}/stats`) : giaLapDanhSach(() => mayChuChungTu.thongKe(), THONG_KE_RONG),

  /** `GET /documents/{id}` — kèm cấu hình loại chứng từ, giá trị từng trường, dòng của các bảng. */
  chiTiet: (id: string): Promise<DocDetail> =>
    dungApiThat() ? goiApi<DocDetail>(goc(id)) : giaLap(() => mayChuChungTu.chiTiet(id)),

  /** `GET /documents/{id}/job-status` (`ocr:job:read`). */
  trangThaiJob: (id: string): Promise<DocJobStatus> =>
    dungApiThat() ? goiApi<DocJobStatus>(`${goc(id)}/job-status`) : giaLap(() => mayChuChungTu.trangThaiJob(id)),

  /** `GET /documents/{id}/transcript` — bản chép từng trang. */
  banChep: (id: string): Promise<DocTranscript> =>
    dungApiThat() ? goiApi<DocTranscript>(`${goc(id)}/transcript`) : giaLap(() => mayChuChungTu.banChep(id)),

  /** `GET /documents/{id}/file[?extra=]` — liên kết ký (`files/sign`) hoặc nội dung tệp (bản dev). */
  tep: async (id: string, extra?: number): Promise<TepXemTruoc> => {
    if (!dungApiThat()) {
      const t = await giaLap(() => mayChuChungTu.tep(id));
      return { kieu: 'noi-dung', ...t };
    }
    const t = await goiApiTepHoacLienKet(`${goc(id)}/file${extra === undefined ? '' : `?extra=${extra}`}`);
    return t.kieu === 'lien-ket' ? t : { ...t, file_name: '' };
  },

  /**
   * `POST /documents/upload` → `202`, multipart: `files` (tối đa 20, 25 MB mỗi tệp, 60 MB tổng),
   * `schema_id`, `language`, `ocr_provider`, `ocr_reader_provider`. Tệp đầu là tệp chính.
   */
  taiLen: (du: DocUploadInput): Promise<DocUploadAccepted> => {
    if (!dungApiThat()) {
      const dau = du.files[0];
      return giaLap(() =>
        mayChuChungTu.taiLen({
          schema_id: du.schema_id,
          soTep: du.files.length,
          tenTep: dau?.name ?? '',
          mime: dau?.type ?? '',
          kichThuoc: dau?.size ?? 0,
        }),
      );
    }
    const bieuMau = new FormData();
    for (const f of du.files) bieuMau.append('files', f, f.name);
    bieuMau.append('schema_id', du.schema_id);
    if (du.language) bieuMau.append('language', du.language);
    if (du.ocr_provider) bieuMau.append('ocr_provider', du.ocr_provider);
    if (du.ocr_reader_provider) bieuMau.append('ocr_reader_provider', du.ocr_reader_provider);
    return goiApi<DocUploadAccepted>(`${GOC}/upload`, { method: 'POST', bieuMau });
  },

  /** `POST /documents/{id}/reprocess` → `202`; trả chứng từ đọc lại sau đó để màn có trạng thái mới. */
  quetLai: (id: string, du: DocReprocessInput): Promise<DocDetail> =>
    dungApiThat()
      ? goiApi<unknown>(`${goc(id)}/reprocess`, { method: 'POST', body: du }).then(() => goiApi<DocDetail>(goc(id)))
      : giaLap(() => mayChuChungTu.quetLai(id, du)),

  /** `POST /documents/{id}/cancel-ocr` — chứng từ thành Lỗi ở bước đang dở. */
  huyQuet: (id: string): Promise<DocDetail> =>
    dungApiThat()
      ? goiApi<DocDetail>(`${goc(id)}/cancel-ocr`, { method: 'POST' })
      : giaLap(() => mayChuChungTu.huyQuet(id)),

  /** `PATCH /documents/{id}` — giá trị trường đã sửa; chỉ ở Nháp hoặc Đã xử lý. */
  sua: (id: string, du: DocPatch): Promise<DocDetail> =>
    dungApiThat()
      ? goiApi<DocDetail>(goc(id), { method: 'PATCH', body: du })
      : giaLap(() => mayChuChungTu.sua(id, du)),

  /** `POST /documents/{id}/confirm`. */
  xacNhan: (id: string): Promise<DocDetail> =>
    dungApiThat()
      ? goiApi<DocDetail>(`${goc(id)}/confirm`, { method: 'POST', body: {} })
      : giaLap(() => mayChuChungTu.xacNhan(id)),

  /**
   * `POST /documents/{id}/transfer` (`ocr:chung-tu:export`). Thao tác nhạy cảm: backend gọi
   * `requireSensitive`, phiên đăng nhập cũ nhận `403` kèm câu yêu cầu xác thực lại.
   */
  chuyen: (id: string): Promise<DocDetail> =>
    dungApiThat()
      ? goiApi<DocDetail>(`${goc(id)}/transfer`, { method: 'POST' })
      : giaLap(() => mayChuChungTu.chuyen(id)),

  /** `DELETE /documents/{id}` → `204`. Không xoá tệp trong kho. */
  xoa: (id: string): Promise<void> =>
    dungApiThat() ? goiApi<void>(goc(id), { method: 'DELETE' }) : giaLap(() => mayChuChungTu.xoa(id)),

  /** `POST /documents/bulk-confirm|bulk-transfer|bulk-delete` — dòng sai trạng thái vào `skipped`. */
  hangLoat: (viec: 'xac-nhan' | 'chuyen' | 'xoa', ids: string[]): Promise<BulkResult> => {
    const duongDan = { 'xac-nhan': 'bulk-confirm', chuyen: 'bulk-transfer', xoa: 'bulk-delete' }[viec];
    return dungApiThat()
      ? goiApi<BulkResult>(`${GOC}/${duongDan}`, { method: 'POST', body: { document_ids: ids } })
      : giaLap(() => mayChuChungTu.hangLoat(viec, ids));
  },
};
